import { formatUtcInTimezone } from '../../lib/time.js';

function daysBetween(a, b) {
  return Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86_400_000);
}

export function CaCard({ ca, stats, viewerTimezone, onDownloadPem, onDownloadCrlPem, onDownloadCrlDer }) {
  if (!ca) {
    return (
      <div className="card animate-pulse px-6 py-12 text-center text-mist">
        <i className="fa-solid fa-shield text-3xl text-mist/50" aria-hidden="true" />
        <div className="mt-3 text-sm">Loading certificate authority…</div>
      </div>
    );
  }
  const daysToExpiry = daysBetween(ca.notAfter, new Date().toISOString());
  const expired = ca.isExpired;

  return (
    <div className="card overflow-hidden">
      <div className="flex items-start gap-4 border-b border-line bg-gradient-to-br from-accent-soft via-white to-canvas px-6 py-5">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-accent text-white shadow-card">
          <i className="fa-solid fa-shield-halved text-2xl" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold text-ink">Certificate Authority</h2>
            <span className="rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent">
              {ca.algorithm}
            </span>
            {expired ? (
              <span className="rounded-full border border-rose-200 bg-rose-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-rose-700">
                Expired
              </span>
            ) : (
              <span className="rounded-full border border-success/30 bg-success-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-success">
                Active
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-mist">{ca.subject}</p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <a
            href={onDownloadPem}
            download="tms-ca.crt"
            className="btn-secondary text-xs"
          >
            <i className="fa-solid fa-file-arrow-down" aria-hidden="true" /> CA cert (PEM)
          </a>
          <a
            href={onDownloadCrlPem}
            download="tms.crl.pem"
            className="btn-secondary text-xs"
          >
            <i className="fa-solid fa-file-shield" aria-hidden="true" /> CRL (PEM)
          </a>
          <a
            href={onDownloadCrlDer}
            download="tms.crl.der"
            className="btn-secondary text-xs"
          >
            <i className="fa-solid fa-file-shield" aria-hidden="true" /> CRL (DER)
          </a>
        </div>
      </div>

      <dl className="grid grid-cols-1 gap-x-6 gap-y-4 px-6 py-5 sm:grid-cols-2 lg:grid-cols-4">
        <Field label="Fingerprint (SHA-256)" mono full>
          <span className="break-all text-[11px]">{ca.fingerprintSha256}</span>
        </Field>
        <Field label="Valid from">
          {formatUtcInTimezone(ca.notBefore, viewerTimezone)}
        </Field>
        <Field label="Valid until">
          <span className={daysToExpiry < 90 ? 'text-warning' : 'text-ink'}>
            {formatUtcInTimezone(ca.notAfter, viewerTimezone)}
          </span>
          {!expired && (
            <span className="ml-2 text-[11px] text-mist">
              ({daysToExpiry} day{daysToExpiry === 1 ? '' : 's'} left)
            </span>
          )}
        </Field>
        <Field label="CRL number" mono>
          {ca.crlNumber}
        </Field>
      </dl>

      {stats && (
        <div className="grid grid-cols-2 gap-px border-t border-line bg-line lg:grid-cols-5">
          <Stat icon="fa-list" label="Issued" value={stats.total} />
          <Stat icon="fa-circle-check" tone="success" label="Active" value={stats.active} />
          <Stat icon="fa-clock" tone="warning" label="Expiring (≤14d)" value={stats.expiringSoon} />
          <Stat icon="fa-hourglass-end" tone="mist" label="Expired" value={stats.expired} />
          <Stat icon="fa-ban" tone="rose" label="Revoked" value={stats.revoked} />
        </div>
      )}
    </div>
  );
}

function Field({ label, children, mono, full }) {
  return (
    <div className={full ? 'sm:col-span-2 lg:col-span-2' : ''}>
      <dt className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</dt>
      <dd className={`mt-0.5 text-sm ${mono ? 'font-mono text-ink' : 'text-ink'}`}>{children}</dd>
    </div>
  );
}

function Stat({ icon, label, value, tone = 'accent' }) {
  const tones = {
    accent:  'text-accent',
    success: 'text-success',
    warning: 'text-warning',
    rose:    'text-rose-700',
    mist:    'text-mist',
  };
  return (
    <div className="flex items-center gap-3 bg-white px-5 py-3">
      <i className={`fa-solid ${icon} ${tones[tone]} text-base`} aria-hidden="true" />
      <div className="leading-tight">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-mist">{label}</div>
        <div className="text-lg font-semibold tracking-tight text-ink">{value}</div>
      </div>
    </div>
  );
}
